import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Client } from '../services/clientService';

interface ClientContextType {
  currentClient: Client | null;
  setCurrentClient: (client: Client | null) => void;
  clientPermissions: string[];
  setClientPermissions: (permissions: string[]) => void;
  refreshClients: () => void;
  clearClientState: () => void;
  canView: () => boolean;
  canCreate: () => boolean;
  canEdit: () => boolean;
  canDelete: () => boolean;
  canManageUsers: () => boolean;
  canManageSettings: () => boolean;
  isOwner: () => boolean;
  isAdmin: () => boolean;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [clientPermissions, setClientPermissions] = useState<string[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const queryClient = useQueryClient();

  // Initialize from localStorage on app startup
  useEffect(() => {
    const savedClientId = localStorage.getItem('current_client_id');
    if (savedClientId) {
      console.log(`Found saved client ID: ${savedClientId}`);
      // Don't clear localStorage here - let ClientSelector handle the restoration
    }
    setIsInitialized(true);
  }, []);

  // Custom setCurrentClient that clears cache
  const setCurrentClientWithCacheClear = (client: Client | null) => {
    console.log(`🔄 ClientContext: Setting current client to ${client?.name} (${client?.id})`);
    // Clear cached data before switching to prevent cross-client data leaks
    queryClient.clear();
    setCurrentClient(client);
  };

  // Update localStorage when current client changes
  useEffect(() => {
    if (currentClient) {
      localStorage.setItem('current_client_id', currentClient.id);
      console.log(`💾 ClientContext: Saved to localStorage - ${currentClient.name} (${currentClient.id})`);
    }
    // Note: We don't automatically clear localStorage when currentClient becomes null
    // This allows the ClientSelector to restore the saved client on app startup
    // localStorage is only cleared explicitly via clearClientState()
  }, [currentClient]);

  const refreshClients = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Add a method to clear client state
  const clearClientState = () => {
    console.log('🗑️ ClientContext: Explicitly clearing client state');
    queryClient.clear(); // Clear cache when clearing client state
    setCurrentClient(null);
    setClientPermissions([]);
    localStorage.removeItem('current_client_id');
  };

  const canView = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin', 'editor', 'member', 'viewer'].includes(role || '');
  };

  const canCreate = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin', 'editor', 'member'].includes(role || '');
  };

  const canEdit = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin', 'editor'].includes(role || '');
  };

  const canDelete = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin'].includes(role || '');
  };

  const canManageUsers = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin'].includes(role || '');
  };

  const canManageSettings = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin'].includes(role || '');
  };

  const isOwner = () => {
    if (!currentClient) return false;
    return currentClient.user_role === 'owner';
  };

  const isAdmin = () => {
    if (!currentClient) return false;
    const role = currentClient.user_role;
    return ['owner', 'admin'].includes(role || '');
  };

  const value: ClientContextType = {
    currentClient,
    setCurrentClient: setCurrentClientWithCacheClear,
    clientPermissions,
    setClientPermissions,
    refreshClients,
    clearClientState,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManageUsers,
    canManageSettings,
    isOwner,
    isAdmin,
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};